import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import EmailAddressRegisterStep from 'src/components/AzarOnly/Login/Steps/Email/EmailAddressRegisterStep';
import RegisterBirthdayStep from 'src/components/AzarOnly/Login/Steps/Register/BirthdayStep';
import useRegister from 'src/components/AzarOnly/Login/useRegister';
import { useRestMutation } from 'src/hooks/useRestMutation';
import { loginRetryDataAtom, loginWithPhoneNumberAtom, retryLoginWithPhoneVerifyAtom, socialTokenAtom } from 'src/stores/auth/atoms';
import { kmcStateAtom } from 'src/stores/kmc/atoms';
import { PhoneNumberLoginApiList, checkAccountExistAPI } from 'src/stores/phoneNumberLogin/apis';
import { handleNonKmcSelfErrorAtom, smsVerificationAtom, smsVerificationResponseAtom, verifyErrorMessages, pushPhoneNumberLoginHistoryAtom } from 'src/stores/phoneNumberLogin/atoms';
import { createKmcVerifyAccountRequest } from 'src/stores/phoneNumberLogin/kmc';
import { VerifyAccountRequest, VerifyAccountResponse } from 'src/stores/phoneNumberLogin/types';
import { birthAtom, LoginModalType, loginModalTypeAtom, pushSocialRegisterHistoryAtom } from 'src/stores/register/atoms';
import { Platform } from 'src/types/Platform';
import { SMS_VERIFIED_METHOD_TYPES } from 'src/types/register';
import { ApiResponse, ErrorAlertMessage, ErrorResponse } from 'src/utils/api';
import { getServerSideLanguageCode } from 'src/utils/language';
export default function useVerifyCode() {
  const setSocialToken = useSetAtom(socialTokenAtom);
  const pushHistory = useSetAtom(pushPhoneNumberLoginHistoryAtom);
  const loginWithPhone = useSetAtom(loginWithPhoneNumberAtom);
  const [smsVerification, setSmsVerification] = useAtom(smsVerificationAtom);
  const setSmsVerificationPhone = useSetAtom(smsVerificationResponseAtom);
  const [loginModalType, setLoginModalType] = useAtom(loginModalTypeAtom);
  const setKmcState = useSetAtom(kmcStateAtom);
  const handleNonKmcSelfError = useSetAtom(handleNonKmcSelfErrorAtom);
  const birth = useAtomValue(birthAtom);
  const pushSocialRegisterHistory = useSetAtom(pushSocialRegisterHistoryAtom);
  const {
    tryRegister
  } = useRegister();
  const {
    mutate: verifyAccount,
    isPending: isVerifyingAccount,
    isSuccess: isVerifyAccountSuccess,
    isError: isVerifyAccountError
  } = useRestMutation<VerifyAccountRequest, ApiResponse<VerifyAccountResponse>, ErrorResponse<ErrorAlertMessage>>({
    method: 'post',
    path: PhoneNumberLoginApiList.VerificationsValidate,
    options: {
      onSuccess: async ({
        data: {
          result
        }
      }, request) => {
        const smsVerificationMethod = request.kmcVerification ? SMS_VERIFIED_METHOD_TYPES.KMC : SMS_VERIFIED_METHOD_TYPES.TSP;
        const isExistAccount = await checkAccountExistAPI(result.phoneId)();
        setSocialToken({
          id: result.phoneId,
          phoneToken: result.phoneToken,
          smsVerification: {
            method: smsVerificationMethod
          }
        });
        setSmsVerificationPhone(result);
        setKmcState(prev => prev.status === 'verified' ? {
          status: 'idle'
        } : prev);
        if (isExistAccount.data.result) {
          loginWithPhone();
        } else {
          if (request.kmcVerification) {
            setLoginModalType(LoginModalType.PHONE_NUMBER_LOGIN);
          }
          pushHistory(<EmailAddressRegisterStep />);
        }
      },
      onError: (error, request) => {
        if (error.response?.data.error.code === 'VERIFICATION_FAILED') {
          setSmsVerification({
            ...smsVerification,
            errorMessage: verifyErrorMessages[error.response.data.error.code] || 'SIGNUP_PHONE_VERIFICATION_FAIL'
          });
        }

        // KMC resultKey 서버 검증 실패는 KMC 자체 에러가 아니므로
        // TSP fallback 대신 로그인 화면 + 에러 토스트로 처리한다.
        if (request.kmcVerification) {
          handleNonKmcSelfError();
        }
      }
    }
  });
  const {
    formattedPhoneNumber
  } = smsVerification;
  const [loginRetryData, setLoginRetryData] = useAtom(loginRetryDataAtom);
  const retryLoginWithPhoneVerify = useSetAtom(retryLoginWithPhoneVerifyAtom);

  // 회원가입 중 NotVerifiedException 에러가 발생했을 때 재시도를 위한 로직
  const retryRegister = (code: string) => {
    const retryPayload = {
      phoneVerification: {
        verifyCode: code,
        formattedPhoneNumber
      }
    };
    return tryRegister(retryPayload);
  };

  // 로그인 중 NotVerifiedException 에러가 발생했을 때 재시도를 위한 로직
  const retryLogin = async (code: string) => {
    if (!loginRetryData) return;
    const retryPayload = {
      phoneVerification: {
        verifyCode: code,
        formattedPhoneNumber
      }
    };
    const originData = JSON.parse(loginRetryData.config.data);
    retryLoginWithPhoneVerify({
      ...loginRetryData.config,
      data: {
        ...originData,
        params: [{
          ...originData.params[0],
          ...retryPayload
        }]
      }
    });
    setLoginRetryData(null);
  };
  const handleVerifyKmc = useCallback((kmcResultKey: string) => {
    setKmcState({
      status: 'idle'
    });
    verifyAccount(createKmcVerifyAccountRequest(kmcResultKey));
  }, [setKmcState, verifyAccount]);
  const handleVerifyCode = async (code: string) => {
    switch (loginModalType) {
      case LoginModalType.SOCIAL_REGISTER:
        {
          // birthday 없이 verifyCode를 마친 경우(KMC fallback/preVerification TSP)는
          // gender까지 입력받은 뒤 registerAtom이 smsVerificationAtom의 인증 code를 읽어 재시도한다.
          if (!birth) {
            pushSocialRegisterHistory(<RegisterBirthdayStep />);
            break;
          }
          await retryRegister(code);
          break;
        }
      case LoginModalType.SOCIAL_LOGIN:
        {
          retryLogin(code);
          break;
        }
      case LoginModalType.PHONE_NUMBER_LOGIN:
        {
          verifyAccount({
            localeInfo: {
              countryCode: null,
              languageCode: getServerSideLanguageCode().toLowerCase()
            },
            phoneVerification: {
              verifyCode: code,
              formattedPhoneNumber
            },
            platform: Platform.WebClient
          });
          break;
        }
      default:
        throw new Error('Invalid loginModalType in handleVerifyCode');
    }
  };
  return {
    handleVerifyCode,
    handleVerifyKmc,
    isLoading: isVerifyingAccount,
    isSuccess: isVerifyAccountSuccess,
    isError: isVerifyAccountError
  };
}